import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
	const readValue = useCallback<() => T>(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			console.error(error);
			return initialValue;
		}
	}, [initialValue, key]);

	const [storedValue, setStoredValue] = useState<T>(readValue());
	const setValue = (value: T | ((val: T) => T)): void => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			const valueToStoreJSON = JSON.stringify(valueToStore);
			if (JSON.stringify(storedValue) !== valueToStoreJSON) {
				localStorage.setItem(key, valueToStoreJSON);
				setStoredValue(valueToStore);
				window.dispatchEvent(new Event('storage'));
			}
		} catch (error) {
			console.error(error);
		}
	};

	const updateValue = useCallback(() => {
		setStoredValue(readValue());
	}, [readValue]);

	useEffect(() => {
		window.addEventListener('storage', updateValue);
		// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
		return () => {
			window.removeEventListener('storage', updateValue);
		};
	}, [readValue, updateValue]);

	return [storedValue, setValue];
}
